/**
 * Utility functions for date formatting without timezone conversion issues
 */

/**
 * Formats a date string to Brazilian locale format without timezone conversion
 * This prevents the common issue where dates are shifted due to UTC/local timezone conversion
 * @param dateString Date in ISO format (YYYY-MM-DD) or Date object
 * @returns Formatted date string in Brazilian format (DD/MM/YYYY) or empty string if invalid
 */
const parseLocalDate = (value: string | Date): Date | null => {
    try {
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }

        // Parse the date string as a local date to avoid timezone conversion
        // This prevents dates like "2025-09-09" from becoming "2025-09-08" in UTC-3 timezone
        const parts = value.split('-');
        const date =
            parts.length === 3
                ? new Date(
                      parseInt(parts[0], 10),
                      parseInt(parts[1], 10) - 1, // Month is 0-based in Date constructor
                      parseInt(parts[2], 10)
                  )
                : new Date(value);

        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
};

export const formatDateSafe = (dateString?: string | null | Date): string => {
    if (!dateString) return '';

    const date = parseLocalDate(dateString);
    return date ? date.toLocaleDateString('pt-BR') : '';
};

/**
 * Formats a date string or Date to Brazilian locale date + time (DD/MM/YYYY às HH:mm)
 * Reuses the same timezone-safe parsing as formatDateSafe
 * @param dateString Date in ISO format (YYYY-MM-DD) or Date object
 * @returns Formatted date+time string or empty string if invalid
 */
export const formatDateTimeSafe = (dateString?: string | null | Date): string => {
    if (!dateString) return '';

    const date = parseLocalDate(dateString);
    if (!date) return '';

    const datePart = date.toLocaleDateString('pt-BR');
    const timePart = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} às ${timePart}`;
};

/**
 * Creates a Date object from a date string without timezone conversion
 * @param dateString Date in ISO format (YYYY-MM-DD)
 * @returns Date object or null if invalid
 */
export const todayIsoBrt = (): string =>
    new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

export const createDateSafe = (dateString?: string | null): Date | null => {
    if (!dateString) return null;
    
    try {
        const parts = dateString.split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-based in Date constructor
            const day = parseInt(parts[2], 10);
            const date = new Date(year, month, day);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return null;
            }
            
            return date;
        }
        return null;
    } catch {
        return null;
    }
};